import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import CommandPalette, { PaletteItem } from './CommandPalette';
import ImpersonationBanner from './ImpersonationBanner';
import NewReportBanner from './NewReportBanner';
import NotificationBell from './NotificationBell';

import {
  Bars3Icon,
  XMarkIcon,
  DocumentTextIcon,
  UsersIcon,
  BuildingOfficeIcon,
  TagIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
  MapIcon,
  ChatBubbleLeftRightIcon,
  MapPinIcon,
  ClockIcon,
  BellIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  DocumentChartBarIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentCheckIcon,
  WrenchScrewdriverIcon,
  BeakerIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';

/* ---------- navigation model ---------- */

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      // { name: 'Dashboard', href: '/dashboard', icon: HomeIcon }, // unmounted, kept for future use
      { name: 'Live Dashboard', href: '/live-dashboard', icon: ChartBarIcon },
      { name: 'Pilot Evidence', href: '/pilot-evidence', icon: BeakerIcon },
    ],
  },
  {
    label: 'Cases',
    items: [
      { name: 'Reports', href: '/reports', icon: DocumentTextIcon },
      { name: 'Assignments', href: '/assignments', icon: ClipboardDocumentListIcon },
      { name: 'Completion Reviews', href: '/resolution-reviews', icon: ClipboardDocumentCheckIcon },
      { name: 'Escalations', href: '/escalations', icon: ExclamationTriangleIcon },
      { name: 'Contractor Performance', href: '/contractor-performance', icon: BuildingOfficeIcon },
      { name: 'Feedback System', href: '/feedback-system', icon: ChatBubbleLeftRightIcon },
    ],
  },
  {
    label: 'Field & Maps',
    items: [
      { name: 'Map', href: '/map', icon: MapIcon },
      { name: 'GIS Analytics', href: '/gis-analytics', icon: MapPinIcon },
      { name: 'Technician Locations', href: '/technician-locations', icon: MapPinIcon },
      { name: 'Service Delivery', href: '/service-delivery', icon: WrenchScrewdriverIcon },
    ],
  },
  {
    label: 'Portals',
    items: [
      { name: 'Councillor Portal', href: '/councillor', icon: UsersIcon },
      { name: 'Contractor Portal', href: '/contractor-portal', icon: BuildingOfficeIcon },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { name: 'Messaging', href: '/messaging', icon: ChatBubbleLeftRightIcon },
      { name: 'Push Notifications', href: '/push-notifications', icon: BellIcon },
      { name: 'Council Packs', href: '/council-packs', icon: DocumentChartBarIcon },
      { name: 'Compliance Reports', href: '/compliance-reports', icon: ClipboardDocumentCheckIcon },
    ],
  },
  {
    label: 'Administration',
    items: [
      { name: 'Staff', href: '/users', icon: UsersIcon },
      { name: 'Residents', href: '/residents', icon: UsersIcon },
      { name: 'Departments', href: '/departments', icon: BuildingOfficeIcon },
      { name: 'Categories', href: '/categories', icon: TagIcon },
      { name: 'Audit Trail', href: '/audit-trail', icon: ClockIcon },
      { name: 'User Sessions', href: '/user-sessions', icon: ClockIcon },
    ],
  },
];

// Role-scoped navigation: portal roles only see their own workspace.
const ROLE_NAV: Record<string, string[]> = {
  councillor: ['Councillor Portal'],
  contractor: ['Contractor Portal'],
};
const ROLE_PORTAL_LABEL: Record<string, string> = {
  councillor: 'Councillor Portal',
  contractor: 'Contractor Portal',
};

const COLLAPSE_KEY = 'nav:collapsed';
const loadCollapsed = (): string[] => {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '[]'); } catch { return []; }
};

/* ---------- component ---------- */

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<string[]>(loadCollapsed);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const allowed = ROLE_NAV[user?.role || ''];
  const groups = useMemo(() => {
    const g = allowed
      ? NAV_GROUPS.map((grp) => ({ ...grp, items: grp.items.filter((i) => allowed.includes(i.name)) }))
      : NAV_GROUPS;
    return g.filter((grp) => grp.items.length > 0);
  }, [allowed]);

  const paletteItems: PaletteItem[] = useMemo(
    () => groups.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label }))),
    [groups]
  );

  const portalLabel = ROLE_PORTAL_LABEL[user?.role || ''] || 'Admin Portal';
  const municipalityName = user?.municipality?.name || 'Municipality';
  const municipalityLogo = user?.municipality?.logoUrl;

  const currentPage = useMemo(
    () => paletteItems.find((i) => i.href === location.pathname),
    [paletteItems, location.pathname]
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (label: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Global ⌘K / Ctrl+K
  const onGlobalKey = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      setPaletteOpen((o) => !o);
    }
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', onGlobalKey);
    return () => window.removeEventListener('keydown', onGlobalKey);
  }, [onGlobalKey]);

  // Close overlays on navigation
  useEffect(() => {
    setSidebarOpen(false);
    setPaletteOpen(false);
  }, [location.pathname]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActiveRoute = (href: string) => location.pathname === href;

  const brand = (
    <div className="flex items-center">
      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-sm bg-white">
        <img
          src={municipalityLogo || '/umngeni-crest.jpg'}
          alt={`${municipalityName} logo`}
          className="h-full w-full object-contain"
        />
      </div>
      <div className="ml-3">
        <div className="text-base font-semibold leading-tight text-white">{municipalityName}</div>
        <div className="text-xs text-primary-300">{portalLabel}</div>
      </div>
    </div>
  );

  const navContent = (
    <nav aria-label="Main navigation" className="scrollbar-thin flex-1 space-y-4 overflow-y-auto px-3 py-4">
      {groups.map((group) => {
        const isCollapsed = collapsed.includes(group.label);
        const hideHeader = groups.length === 1;
        return (
          <div key={group.label}>
            {!hideHeader && (
              <button
                type="button"
                onClick={() => toggleGroup(group.label)}
                aria-expanded={!isCollapsed}
                className="flex w-full items-center justify-between rounded px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-400 hover:text-primary-200"
              >
                {group.label}
                <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
              </button>
            )}
            {!isCollapsed && (
              <div className="mt-1 space-y-0.5">
                {group.items.map((item) => {
                  const active = isActiveRoute(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      aria-current={active ? 'page' : undefined}
                      className={`group relative flex items-center rounded-sm px-2.5 py-2 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-primary-700 text-white'
                          : 'text-primary-200 hover:bg-primary-700/50 hover:text-white'
                      }`}
                    >
                      {active && <span aria-hidden className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-accent-500" />}
                      <item.icon className={`mr-3 h-5 w-5 shrink-0 ${active ? 'text-accent-400' : 'text-primary-400 group-hover:text-accent-400'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ImpersonationBanner />
      <NewReportBanner />

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <div className="fixed inset-0 bg-gray-900/50 animate-fade-in" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-primary-800 shadow-pop animate-slide-up">
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-primary-700 px-4">
              {brand}
              <button type="button" aria-label="Close menu" className="rounded p-1 text-primary-300 hover:text-white" onClick={() => setSidebarOpen(false)}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {navContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col bg-primary-800">
          <div className="flex h-16 shrink-0 items-center border-b border-primary-700 px-4">{brand}</div>
          {navContent}
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-3 border-b-2 border-primary-800 bg-white/90 px-4 backdrop-blur dark:bg-gray-900/90 sm:gap-x-4 sm:px-6 lg:px-8">
          <button type="button" aria-label="Open menu" className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="h-6 w-6" />
          </button>

          <h2 className="hidden truncate text-sm font-semibold text-gray-900 dark:text-gray-100 md:block">
            {currentPage?.name || ''}
          </h2>

          <div className="flex flex-1 justify-center px-2">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex w-full max-w-md items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
              aria-label="Search pages (Command K)"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              <span className="flex-1 text-left">Search or jump to…</span>
              <kbd className="hidden rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-400 dark:border-gray-600 dark:bg-gray-900 sm:block">⌘K</kbd>
            </button>
          </div>

          <div className="flex items-center gap-1">
            <NotificationBell />

            <button
              type="button"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={toggleTheme}
              className="rounded-sm p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            {/* User menu */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                type="button"
                className="flex items-center gap-x-2 rounded-full text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                aria-haspopup="menu"
                aria-expanded={profileDropdownOpen}
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              >
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.firstName} {user?.lastName}</p>
                  <span className="inline-block rounded-sm border border-accent-600 bg-white px-1.5 py-0.5 text-[10px] font-medium capitalize text-accent-800 dark:bg-gray-900">{user?.role}</span>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </button>

              {profileDropdownOpen && (
                <div role="menu" className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg bg-white py-1 shadow-pop ring-1 ring-gray-200 animate-slide-up dark:bg-gray-800 dark:ring-gray-700">
                  <button
                    role="menuitem"
                    onClick={() => { setProfileDropdownOpen(false); navigate('/profile'); }}
                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Cog6ToothIcon className="mr-3 h-4 w-4 text-gray-400" />
                    Edit Profile
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setProfileDropdownOpen(false); handleLogout(); }}
                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4 text-gray-400" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="py-6 text-gray-900 dark:text-gray-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />
    </div>
  );
};

export default Layout;
